/**
 * materials3d - material-Enum + defaultStyle → ruhige 3D-Farb-Specs (#600)
 *
 * Reine Logik. Farbwahl: gedeckte, ruhige Töne (Markenton, Emerald-Palette-
 * verträglich) statt der teils kräftigen 2D-Editorfarben. Custom-Color des
 * Nutzers (data.useCustomColor) gewinnt immer.
 *
 * Spec-Form: { color, opacity, transparent } — die Meshes ergänzen
 * roughness/metalness selbst (einheitlich matt).
 */

import { GARDEN_OBJECT_TYPES_CONFIG, getColorPropertyKey } from '../config/gardenObjectTypes';

/** Werte des material-Selects (gardenObjectTypes.js) → Farbe (+ Transparenz). */
export const MATERIAL_COLORS = {
  // Wege / Beläge
  kies: { color: '#B8B2A4' },
  pflaster: { color: '#A8A29E' },
  holz: { color: '#9A7B52' },
  rindenmulch: { color: '#6B4F3A' },
  naturstein: { color: '#8F8A80' },
  fliesen: { color: '#CFCACC' },
  wpc: { color: '#8B7355' },
  beton: { color: '#A3A3A3' },
  stein: { color: '#8F8A80' },

  // Mauer
  brick: { color: '#9C6B5A' },
  stone: { color: '#78716C' },
  concrete: { color: '#A3A3A3' },
  gabion: { color: '#8A8D8F' },

  // Zaun / Tor
  metall: { color: '#9CA3AF' },
  kunststoff: { color: '#D4D4D8' },
  draht: { color: '#C0C4C8' },
  schmiedeeisen: { color: '#3F3F46' },
  aluminium: { color: '#C7CCD1' },

  // Gewächshaus (transparent)
  glas: { color: '#DCEFF5', opacity: 0.35, transparent: true },
  polycarbonat: { color: '#E4F0EC', opacity: 0.45, transparent: true },
  acrylglas: { color: '#E8F2F5', opacity: 0.4, transparent: true },
  folie: { color: '#EDF3EE', opacity: 0.55, transparent: true },

  // Gefäße / Hochbeet
  terracotta: { color: '#C4704F' },
  keramik: { color: '#D6CFC7' },
  kokos: { color: '#A98555' },
  rattan: { color: '#B08D57' },
  paletten: { color: '#A5804E' },
};

/** Kind-basierte Specs (nicht material-getrieben). */
export const KIND_MATERIALS = {
  // Dunkler und deckender als bis #694 (#7FB4D9 / 0,75): der durchscheinende
  // Muldengrund ließ jeden Teich flach wirken. Rest-Transparenz bleibt, damit
  // Wasser Wasser bleibt und die Render-Reihenfolge sich nicht ändert.
  wasser: { color: '#5E8CA8', opacity: 0.88, transparent: true },
  erde: { color: '#5E4632' },
  lawn: { color: '#8FAE7A' },
  'door-panel': { color: '#4E5A54' }, // abgesetztes Tür-Paneel
  // Fenster-Glas (#690): transparent, an der Gewächshaus-Glaspalette orientiert,
  // damit es sich als Glasfläche von der Wand abhebt statt als zweite Tür.
  'window-panel': { color: '#CFE6EF', opacity: 0.4, transparent: true },
};

/**
 * Wasserton je Teich-Typ (#700 teich.type). Zierteich/Fischteich behalten den
 * ruhigen Default-Wasserton (KIND_MATERIALS.wasser); Pool/Schwimmteich wirken
 * klarer und türkiser, Bachlauf/Quellstein heller (flaches, bewegtes Wasser).
 * Eine ausdrückliche Nutzerfarbe (useCustomColor) übersteuert weiterhin alles.
 */
export const POND_WATER_BY_TYPE = {
  pool: { color: '#2FA6C6', opacity: 0.7, transparent: true },
  schwimmteich: { color: '#3F97AC', opacity: 0.82, transparent: true },
  bachlauf: { color: '#7FB4C9', opacity: 0.78, transparent: true },
  quellstein: { color: '#7FB4C9', opacity: 0.78, transparent: true },
};

const FALLBACK = { color: '#B0ACA4' };

function normalizeSpec(spec) {
  return {
    color: spec.color,
    opacity: spec.opacity ?? 1,
    transparent: spec.transparent ?? false,
  };
}

/** defaultStyle.fill des Objekttyps aus der zentralen Config. */
export function getConfigFill(typeId) {
  const entry = Object.values(GARDEN_OBJECT_TYPES_CONFIG).find((t) => t.id === typeId);
  const fill = entry?.defaultStyle?.fill;
  return fill && fill !== 'transparent' ? fill : entry?.defaultStyle?.stroke || null;
}

/**
 * Material-Spec für ein Objekt.
 * Priorität: ausdrückliche Nutzerfarbe → material-Enum → defaultStyle → Fallback.
 *
 * Der Farbschlüssel wird aus der Config abgeleitet (getColorPropertyKey), statt
 * fest `data.color` anzunehmen: Das Gebäude speichert seine Farbe als
 * `wallColor` — nur über die Ableitung erreicht diese Wandfarbe die 3D-Ansicht
 * (#691). Für alle anderen Typen ist der Schlüssel `color`, das Verhalten also
 * unverändert.
 *
 * @param {string} typeId - Garten-Objekttyp-ID (weg, mauer, …)
 * @param {object} [data] - obj.data aus dem Snapshot (material, color/wallColor, useCustomColor)
 * @returns {{color: string, opacity: number, transparent: boolean}}
 */
export function getMaterialSpec(typeId, data = {}) {
  const colorKey = getColorPropertyKey(typeId);
  const userColor = colorKey ? data[colorKey] : data.color;
  if (data.useCustomColor && userColor) {
    return normalizeSpec({ color: userColor });
  }
  const material = data.material;
  if (material && MATERIAL_COLORS[material]) {
    return normalizeSpec(MATERIAL_COLORS[material]);
  }
  const configFill = getConfigFill(typeId);
  if (configFill) {
    return normalizeSpec({ color: configFill });
  }
  return normalizeSpec(FALLBACK);
}

/** Kind-Spec (wasser, erde, door-panel, …). */
export function getKindMaterial(kind) {
  return normalizeSpec(KIND_MATERIALS[kind] ?? FALLBACK);
}

/**
 * Material-Spec einer Terrassen-Überdachung (#693 dachMaterial): Glas /
 * Polycarbonat / Holz greifen direkt auf MATERIAL_COLORS zu — Glas und
 * Polycarbonat bleiben dabei transparent, damit ein Terrassendach als
 * lichtdurchlässige Fläche liest. Unbekannt → Fallback. Anders als
 * getMaterialSpec ist hier KEIN useCustomColor-Pfad gewollt: die Dachfarbe
 * folgt allein dem Dachmaterial, nicht der (Boden-)Farbe der Terrasse.
 */
export function getRoofMaterialSpec(dachMaterial) {
  return normalizeSpec(MATERIAL_COLORS[dachMaterial] ?? FALLBACK);
}

/**
 * Farb-Spec fürs Gebäudedach (#722 roofColor): eine schlichte Volltonfarbe,
 * unabhängig von der Wandfarbe. Anders als getMaterialSpec kein material-Enum-
 * und kein useCustomColor-Pfad — die Dachfarbe wirkt immer. Der Aufrufer löst
 * den Config-Default vorab auf (buildSceneModel via getConfigDefault); ist trotz
 * allem nichts gesetzt, greift der neutrale Fallback.
 */
export function getRoofColorSpec(roofColor) {
  return normalizeSpec(roofColor ? { color: roofColor } : FALLBACK);
}

/** Wasserton für einen Teich-Typ (#700); Default = ruhiger Wasserton. */
export function getPondWaterSpec(type) {
  return normalizeSpec(POND_WATER_BY_TYPE[type] ?? KIND_MATERIALS.wasser);
}
